import React, { useState, useRef } from 'react'

function csrfToken(){
  let matches = (document.cookie+';').match(/csrftoken=([A-z0-9]+);/)
  return matches[1]
}

function slugify(text) { // Google AI answer, didn't check
  return text
    .toString()                     // Ensure it's a string
    .normalize('NFD')               // Split accented characters into baseline characters + diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove all diacritic marks
    .toLowerCase()                  // Convert to lowercase
    .trim()                         // Trim leading/trailing whitespace
    .replace(/\s+/g, '-')           // Replace spaces with hyphens
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars (except hyphens)
    .replace(/\-\-+/g, '-');        // Replace multiple hyphens with a single one
}


function TrialForm(){
  const [siteName, setSiteName] = useState('');
  const [pendingChanges, setPendingChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [template, setTemplate] = useState(null);
  const [email, setEmail] = useState(null);

  let timer = useRef();
  const updateSiteName = (siteName, delay=3000) => {
    const slug = slugify(siteName)
    setSiteName(siteName)
    setSlugAvailable(false)
    setPendingChanges(true)
    if (timer.current) {
      clearTimeout(timer.current)
    }
    timer.current = setTimeout(() => checkSlug(slug), delay)
  }

  const checkSlug = async (slug) => {
    if (slug.length == 0){
      setLoading(false)
      setPendingChanges(false)
      setSlugAvailable(false)
      return
    }
    setLoading(true)
    try{
      const resp = await fetch('/products/check-slug/', {
        method: 'post',
        credentials: 'same-origin',
        headers: { 
          'X-CSRFToken': csrfToken(),
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({slug}),
      })
      const data = await resp.json()
      console.log(data)
      setLoading(false)
      setPendingChanges(false)
      setSlugAvailable(data[slug])
    } catch (error){
      console.log('Could not check slug?', error)
    } finally {
      setLoading(false);
    }
  }


  const doOrder = async (event) => {
    console.log("doOrder")
    event.preventDefault()
    setLoading(true)
    let slug = slugify(siteName)
    try {
      const resp = await fetch('order/', {
        method: 'post',
        credentials: 'same-origin',
        headers: { 
          'X-CSRFToken': csrfToken(),
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            slug,
            template,
            email,
        }),
      })
      if (resp.redirected) {
        window.location.href = resp.url
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }


  return (
    <div className="flex items-center justify-center p-4">
      <form className="max-w-100" onSubmit={doOrder}>
        <div className="form-control">
          <label className="label">
            <span className="font-medium text-stone-600 pt-2">Pick a name for your site</span>
          </label>
          <input
            type="text"
            placeholder="My Site"
            className="input input-bordered w-full"
            value={siteName}
            onChange={(e) => updateSiteName(e.target.value)}
            disabled={loading || result}
          />
        </div>
        <div className="form-control">
          <label className="label">
            <span className="font-medium text-stone-600 pt-2">Site slug</span>
          </label>
          <div className="mt-1 flex rounded-md border border-stone-300 bg-white px-3 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500">
            <input
              type="text"
              placeholder="my-site"
              className="border-0 p-0 pl-2 focus:ring-0"
              name="slug"
              value={slugify(siteName)}
              disabled={true}
            />
            <span className="text-stone-500 w-full">.launch-pages.app</span>
            { pendingChanges && !loading && 
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                  <path d="M8 16H3v5" />
                </svg>
            }
            { loading && <span className="loading loading-spinner"></span> }
            { !pendingChanges && !loading && slugAvailable && 
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-green-500" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 6 9 18 2 12" />
              </svg>}
            { !pendingChanges && !loading && !slugAvailable && slugify(siteName).length > 0 && <i>no</i> }
          </div>
        </div>

        <fieldset>
          <legend className="block font-medium text-stone-600 pt-2">Choose a template</legend>
          <div className="grid grid-cols-2 gap-3 pb-2">
            <label className="group relative cursor-pointer">
              <input 
                type="radio"
                name="layout"
                value="basic-site"
                className="peer sr-only"
                checked={template == 'basic-site'}
                onChange={() => setTemplate('basic-site')}
              />
              <div className="rounded-xl border-2 border-stone-200 bg-stone-50 p-4 flex flex-col items-center gap-2 transition peer-checked:border-blue-500 peer-checked:bg-blue-50 peer-checked:ring-2 peer-checked:ring-blue-200 hover:border-stone-300">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-stone-500 peer-checked:text-blue-600 group-has-[:checked]:text-blue-600">
                  <rect x="3" y="3" width="8" height="8" rx="1.5"/><rect x="13" y="3" width="8" height="8" rx="1.5"/>
                  <rect x="3" y="13" width="8" height="8" rx="1.5"/><rect x="13" y="13" width="8" height="8" rx="1.5"/>
                </svg>
                <span className="text-xs font-medium text-stone-600 group-has-[:checked]:text-blue-700">Basic site</span>
              </div>
              <div className="hidden peer-checked:flex absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-blue-500 items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white" className="w-3 h-3">
                  <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-3.5-3.5a1 1 0 1 1 1.4-1.4l2.8 2.8 6.8-6.8a1 1 0 0 1 1.4 0Z" clipRule="evenodd"/>
                </svg>
              </div>
            </label>
     
            <label className="group relative cursor-pointer">
              <input 
                type="radio"
                name="layout"
                value="coming-soon"
                className="peer sr-only"
                checked={template == 'coming-soon'}
                onChange={() => setTemplate('coming-soon')}
              />
              <div className="rounded-xl border-2 border-stone-200 bg-stone-50 p-4 flex flex-col items-center gap-2 transition peer-checked:border-blue-500 peer-checked:bg-blue-50 peer-checked:ring-2 peer-checked:ring-blue-200 hover:border-stone-300">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-stone-500 peer-checked:text-blue-600 group-has-[:checked]:text-blue-600">
                  <rect x="3" y="4" width="18" height="4" rx="1"/><rect x="3" y="10" width="18" height="4" rx="1"/><rect x="3" y="16" width="18" height="4" rx="1"/>
                </svg>
                <span className="text-xs font-medium text-stone-600 group-has-[:checked]:text-blue-700">Coming soon</span>
              </div>
              <div className="hidden peer-checked:flex absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-blue-500 items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white" className="w-3 h-3">
                  <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-3.5-3.5a1 1 0 1 1 1.4-1.4l2.8 2.8 6.8-6.8a1 1 0 0 1 1.4 0Z" clipRule="evenodd"/>
                </svg>
              </div>
            </label>
     
          </div>
        </fieldset>

        <div className="form-control">
          <label className="label">
            <span className="font-medium text-stone-600 pt-2">What is your email address</span>
          </label>
          <input
            type="email"
            placeholder="you@mail.com"
            name="email"
            className="input input-bordered w-full"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading || result}
          />
        </div>

        <button 
          type="submit" 
          className="btn btn-primary w-full mt-2"
          disabled={loading || pendingChanges || !slugAvailable || slugify(siteName).length == 0 || !template || template.length == 0 || !email || email.length == 0 }
        >
          {loading ? (
            <>
              <span className="loading loading-spinner"></span>
              Checking...
            </>
          ) : (
            result ? ( 
              'Reset' 
            ) : (
              'Create site'
            )
          )}
        </button>
      </form>
    </div>
  )
}

function ProductTrial() {
  return (
    <>
      <TrialForm />
    </>
  )
}

export default ProductTrial
